import type { ApprovalMode, ContractRiskLevel, EmitEvent } from "@/domains/core"
import type { EventEmitter } from "@/domains/library"
import type { Address } from "viem"
import type { Config } from "wagmi"
import { weiValueToBigNumber } from "@/domains/core"
import { MinimalPoolStore } from "@/domains/minimal-pool"
import { approveStakePairAllowance, stakeLiquidity } from "@/domains/on-chain"
import { parseUnits } from "viem"

type StakeLiquidityActionInteraction = {
  amountWei: bigint
  approvalMode: ApprovalMode
  chainId: number
  config: Config
  connectedAddress: Address
  contractRiskLevel: ContractRiskLevel
  emitter: EventEmitter<EmitEvent>
  minimalPool: MinimalPoolStore
  onSuccess: () => void
}

export const stakeLiquidityActionInteraction = async ({
  connectedAddress,
  chainId,
  config,
  emitter,
  amountWei,
  minimalPool,
  approvalMode,
  contractRiskLevel,
  onSuccess,
}: StakeLiquidityActionInteraction) => {
  if (!connectedAddress) {
    return emitter.emit("error-not-connected", {
      title: "Not connected",
      description: "In order to interact with this app, you first must connect your wallet.",
      variant: "error",
    })
  }

  if (!minimalPool) {
    emitter.emit("error-invalid-minimal-pool", {
      title: "Invalid minimal pool",
      description: "Unable to stake liquidity - pair information is missing",
      variant: "error",
    })
    return
  }

  if (!minimalPool.gaugeAddress) {
    emitter.emit("error-invalid-minimal-pool", {
      title: "Invalid minimal pool",
      description: "Unable to stake liquidity - gauge address is missing",
      variant: "error",
    })
    return
  }

  // --------------------------------------------------------------------------------
  // STEP ONE: Check if the user has enough balance to stake LP
  if (minimalPool.userBalance < amountWei) {
    emitter.emit("error-insufficient-balance-lp", {
      title: "Insufficient balance",
      description: `You have ${minimalPool.displayBalance} ${minimalPool.displayName} but you are trying to spend ${minimalPool.formatUnits(amountWei)}.`,
      variant: "error",
    })
    return
  }

  // Request the desired allowance
  if (minimalPool.onChainUserGaugeAllowance < amountWei) {
    const pairApproveRequest = await approveStakePairAllowance({
      account: connectedAddress,
      chainId,
      config,
      pairAddress: minimalPool.address.toViemAddress(),
      gaugeAddress: minimalPool.gaugeAddress.toViemAddress(),
      amount: amountWei,
      approvalMode,
      contractRiskLevel,
    })

    if (pairApproveRequest.success) {
      minimalPool.onChainUserGaugeAllowance = amountWei
    }

    if (!pairApproveRequest.success) {
      emitter.emit("error-approve-stake", {
        title: `Unable to approve ${minimalPool.displayName} allowance`,
        description: pairApproveRequest.error || "Unknown error",
        variant: "error",
      })
      return
    }
  }

  // --------------------------------------------------------------------------------
  // STEP THREE: Stake the LP
  const depositLiquidityResult = await stakeLiquidity({
    account: connectedAddress,
    chainId,
    config,
    gaugeAddress: minimalPool.gaugeAddress.toViemAddress(),
    amount: amountWei,
  })

  if (depositLiquidityResult.success) {
    onSuccess?.()
    emitter.emit("success-stake-liquidity", {
      title: `Stake ${minimalPool.displayName} liquidity`,
      description: `Staked ${minimalPool.formatUnits(amountWei)} in the ${minimalPool.displayName} gauge`,
      variant: "success",
    })
  } else {
    emitter.emit("error-stake-liquidity", {
      title: `Unable to stake ${minimalPool.displayName} in the gauge`,
      description: depositLiquidityResult.error || "Unknown error",
      variant: "error",
    })
    return depositLiquidityResult
  }
  return depositLiquidityResult
}
